import { BotMode, LanguagesEnum } from 'types/index'
import { Setting, Theme } from 'types/setting'

/**
 * Get user token from URL query params
 */
export const getUserToken = (): string => {
  const searchParams = new URLSearchParams(window.location?.search)
  return searchParams.get('token') ?? ''
}

export const getBotMode = (): BotMode => {
  const searchParams = new URLSearchParams(window.location?.search)
  return searchParams.get('mode') as BotMode
}

export const getAreaIdSearchParams = (): string | undefined => {
  const searchParams = new URLSearchParams(window.location?.search)
  const areaId = searchParams.get('areaId')
  return areaId ? String(areaId) : undefined
}

export const getAreaSlugSearchParams = (): string | undefined => {
  const searchParams = new URLSearchParams(window.location?.search)
  const areaSlug = searchParams.get('areaSlug')
  return areaSlug ? String(areaSlug) : undefined
}

/**
 * Get language from URL query params
 */
export const getLang = (): LanguagesEnum => {
  const lang = new URLSearchParams(window.location?.search).get('lang') ?? ''

  return (lang ?? 'it') as LanguagesEnum
}

/**
 * Read the `?theme=` override from URL query params. Returns null if absent or
 * invalid, so the caller can fall back to the server setting (param wins).
 */
export const getThemeParam = (): Theme | null => {
  const theme = new URLSearchParams(window.location?.search).get('theme')
  return theme === 'dark' || theme === 'light' || theme === 'auto' ? theme : null
}

/**
 * When `?forceAgentic=true` is set (used by the backoffice agent demo), the agentic
 * mode is kept on even if some required runtime inputs are missing — so the agent
 * can always be tested. In normal embeds it stays off and the missing-input gating
 * applies.
 */
export const getForceAgenticSearchParams = (): boolean =>
  new URLSearchParams(window.location?.search).get('forceAgentic') === 'true'

/**
 * `?history=true` on /notebook opens the chat history sidebar at start. Off by default: the
 * notebook is usually embedded in a narrow host panel where the history would cover the chat.
 */
export const getNotebookHistoryParam = (): boolean =>
  new URLSearchParams(window.location?.search).get('history') === 'true'

/**
 * Read a stringified JSON settings override from `?config=` query param.
 * Returns null if absent or malformed.
 */
export const getSettingsOverride = (): Partial<Setting> | null => {
  const raw = new URLSearchParams(window.location?.search).get('config')
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Partial<Setting>
    }
    return null
  } catch {
    console.warn('[query-params] invalid ?config= JSON, ignoring override')
    return null
  }
}
